import { createContext, useCallback, useContext, useState } from "react";
import Cookies from "js-cookie";
import { useServerFn } from "@tanstack/react-start";
import { useLayoutEffect } from "@tanstack/react-router";
import { getDefaultAppearance } from "@/handlers/getDefaultAppearance";

const ThemeContext = createContext<{
	theme: "light" | "dark";
	toggleTheme: () => void;
}>({
	theme: "light",
	toggleTheme: () => {},
});

export function ThemeProvider({
	children,
	defaultTheme,
}: {
	children: React.ReactNode;
	defaultTheme?: "light" | "dark";
}) {
	const getAppearance = useServerFn(getDefaultAppearance);
	const [theme, setTheme] = useState<"light" | "dark">(defaultTheme || "light");

	const getDefaultTheme = useCallback(() => {
		if (window?.matchMedia) {
			return window.matchMedia("(prefers-color-scheme: dark)").matches
				? "dark"
				: "light";
		}

		return "light";
	}, []);

	useLayoutEffect(() => {
		if (!defaultTheme) {
			getAppearance().then((res) => {
				if (res) {
					setTheme(res);
				} else {
					setTheme(getDefaultTheme());
				}
			});
		}
	});

	const toggleTheme = useCallback(() => {
		if (theme === "light") {
			document.startViewTransition(() => {
				setTheme("dark");
			});
			Cookies.set("APPEARANCE", "dark");
		} else {
			document.startViewTransition(() => {
				setTheme("light");
			});
			Cookies.set("APPEARANCE", "light");
		}
	}, [theme]);

	return (
		<ThemeContext.Provider value={{ theme, toggleTheme }}>
			{children}
		</ThemeContext.Provider>
	);
}

export function useTheme() {
	return useContext(ThemeContext);
}
